
import { useState } from 'react';
import BackendLayout from '../../components/feature/BackendLayout';
import { mockDashboardStats, mockProducts, mockAffiliateLinks } from '../../mocks/products';

export default function DashboardPage() {
  const [timeRange, setTimeRange] = useState('30d');

  return (
    <BackendLayout>
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Dashboard</h1>
          <p className="text-sm text-gray-500">Welcome back! Here&apos;s your performance overview</p>
        </div>
        <div className="flex items-center gap-2">
          {['7d', '30d', '90d', '1y'].map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                timeRange === range
                  ? 'bg-[#0A0A0A] text-white'
                  : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-5 mb-8">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-money-dollar-circle-line text-xl text-emerald-600"></i>
            </div>
            <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">
              +{mockDashboardStats.monthlyGrowth}%
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-0.5">
            ${mockDashboardStats.totalRevenue.toLocaleString()}
          </div>
          <div className="text-xs text-gray-500">Total Revenue</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-sky-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-cursor-line text-xl text-sky-600"></i>
            </div>
            <span className="text-xs font-semibold text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full">
              +12.3%
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-0.5">
            {mockDashboardStats.totalClicks.toLocaleString()}
          </div>
          <div className="text-xs text-gray-500">Total Clicks</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-exchange-line text-xl text-amber-600"></i>
            </div>
            <span className="text-xs font-semibold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              +8.7%
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-0.5">
            {mockDashboardStats.totalConversions.toLocaleString()}
          </div>
          <div className="text-xs text-gray-500">Conversions</div>
        </div>

        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-teal-50 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-percent-line text-xl text-teal-600"></i>
            </div>
            <span className="text-xs font-semibold text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full">
              +2.1%
            </span>
          </div>
          <div className="text-2xl font-bold text-gray-900 mb-0.5">
            {mockDashboardStats.conversionRate}%
          </div>
          <div className="text-xs text-gray-500">Conversion Rate</div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
        {/* Revenue Chart */}
        <div className="lg:col-span-2 bg-white rounded-xl p-6 shadow-sm border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-base font-bold text-gray-900">Revenue Overview</h3>
            <a href="/affiliate-links" className="text-xs text-gray-500 hover:text-gray-900 cursor-pointer whitespace-nowrap">
              View Report <i className="ri-arrow-right-line"></i>
            </a>
          </div>
          <div className="h-56 flex items-end justify-between gap-2">
            {[65, 45, 80, 55, 90, 70, 85, 60, 95, 75, 88, 92].map((height, i) => (
              <div key={i} className="flex-1 flex flex-col items-center gap-2">
                <div
                  className="w-full bg-gradient-to-t from-[#0A0A0A] to-gray-600 rounded-t-md hover:from-[#00FF88] hover:to-emerald-400 transition-all cursor-pointer"
                  style={{ height: `${height}%` }}
                ></div>
                <span className="text-[10px] text-gray-400">{i + 1}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Top Performer */}
        <div className="bg-[#0A0A0A] rounded-xl p-6 shadow-lg text-white">
          <h3 className="text-base font-bold mb-5">Top Performer</h3>
          <div className="mb-5">
            <div className="text-2xl font-bold mb-1">{mockDashboardStats.topPerformer}</div>
            <div className="text-xs text-white/50">Highest earning product this month</div>
          </div>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Revenue</span>
              <span className="text-sm font-bold text-[#00FF88]">$12,015</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Conversions</span>
              <span className="text-sm font-bold">267</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-xs text-white/50">Commission</span>
              <span className="text-sm font-bold">45%</span>
            </div>
          </div>
          <button className="w-full mt-5 py-2.5 bg-white/10 text-white text-sm font-semibold rounded-lg hover:bg-white/20 transition-all whitespace-nowrap cursor-pointer">
            View Details
          </button>
        </div>
      </div>

      {/* Recent Links */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden mb-8">
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-base font-bold text-gray-900">Recent Affiliate Links</h3>
            <a href="/affiliate-links" className="text-xs text-teal-600 hover:text-teal-700 font-semibold cursor-pointer whitespace-nowrap">
              View All <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Product</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Short URL</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Clicks</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Conversions</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Revenue</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {mockAffiliateLinks.slice(0, 5).map((link) => (
                <tr key={link.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-5 py-3.5">
                    <div className="text-sm font-medium text-gray-900">{link.productName}</div>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-teal-600 font-mono">{link.shortUrl}</span>
                      <button className="text-gray-400 hover:text-gray-600 cursor-pointer">
                        <i className="ri-file-copy-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-sm text-gray-900">{link.clicks.toLocaleString()}</td>
                  <td className="px-5 py-3.5 text-sm text-gray-900">{link.conversions}</td>
                  <td className="px-5 py-3.5">
                    <span className="text-sm font-semibold text-emerald-600">${link.revenue.toLocaleString()}</span>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${
                      link.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {link.status}
                    </span>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-1">
                      <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-all cursor-pointer">
                        <i className="ri-edit-line text-sm"></i>
                      </button>
                      <button className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-all cursor-pointer">
                        <i className="ri-delete-bin-line text-sm"></i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Products */}
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <h3 className="text-base font-bold text-gray-900 mb-5">Top Performing Products</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {mockProducts.slice(0, 3).map((product, index) => (
            <div
              key={product.id}
              className="relative bg-gray-50 rounded-xl p-5 border border-gray-100 hover:shadow-md transition-all cursor-pointer"
            >
              <div className="absolute top-4 right-4 w-7 h-7 bg-[#0A0A0A] text-white rounded-full flex items-center justify-center text-xs font-bold">
                #{index + 1}
              </div>
              <h4 className="text-sm font-bold text-gray-900 mb-1 pr-8">{product.name}</h4>
              <p className="text-xs text-gray-500 mb-4">{product.category}</p>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xl font-bold text-emerald-600">${product.earnings}</div>
                  <div className="text-[10px] text-gray-400">Revenue</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-gray-900">{product.conversions}</div>
                  <div className="text-[10px] text-gray-400">Conversions</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </BackendLayout>
  );
}
